/**
 * Technical Approach Creation Modal
 * Form-based technical approach creation
 */

import React, { useState } from 'react';
import { Card, Button, Input, Badge } from '@tracekit/core';
import { Lightbulb, X, CheckCircle } from 'lucide-react';
import { useToastStore } from '@tracekit/core';

interface TechnicalApproachCreationModalProps {
  onClose: () => void;
}

const APPROACH_CATEGORIES = [
  {
    id: 'sampling',
    name: 'Sampling & Testing',
    description: 'Methods for environmental sampling and laboratory analysis',
    icon: '🔬',
    color: 'from-blue-500 to-cyan-600',
    examples: ['Soil sampling', 'Groundwater monitoring', 'Air quality testing'],
  },
  {
    id: 'remediation',
    name: 'Remediation Technology',
    description: 'Cleanup and treatment technologies',
    icon: '♻️',
    color: 'from-green-500 to-emerald-600',
    examples: ['Bioremediation', 'Excavation', 'In-situ treatment'],
  },
  {
    id: 'monitoring',
    name: 'Monitoring & Surveillance',
    description: 'Long-term monitoring and compliance verification',
    icon: '📊',
    color: 'from-purple-500 to-indigo-600',
    examples: ['Continuous monitoring', 'Periodic inspections', 'Data logging'],
  },
  {
    id: 'engineering',
    name: 'Engineering Controls',
    description: 'Engineering solutions and design approaches',
    icon: '⚙️',
    color: 'from-orange-500 to-red-600',
    examples: ['Containment systems', 'Treatment systems', 'Cap design'],
  },
  {
    id: 'assessment',
    name: 'Risk Assessment',
    description: 'Risk characterization and assessment methodologies',
    icon: '🎯',
    color: 'from-pink-500 to-rose-600',
    examples: ['QHRA', 'Exposure modeling', 'Fate & transport'],
  },
  {
    id: 'other',
    name: 'Other Approach',
    description: 'Custom or specialized technical approach',
    icon: '✨',
    color: 'from-gray-500 to-slate-600',
    examples: [],
  },
];

export function TechnicalApproachCreationModal({ onClose }: TechnicalApproachCreationModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<typeof APPROACH_CATEGORIES[0] | null>(null);
  const [approachName, setApproachName] = useState('');
  const [description, setDescription] = useState('');
  const [methodology, setMethodology] = useState('');
  const { addToast } = useToastStore();

  const handleSelectCategory = (category: typeof APPROACH_CATEGORIES[0]) => {
    setSelectedCategory(category);
  };

  const handleCreate = () => {
    if (!selectedCategory || !approachName.trim() || !description.trim()) {
      addToast({
        type: 'error',
        title: 'Missing Information',
        message: 'Please fill in all required fields',
      });
      return;
    }

    addToast({
      type: 'success',
      title: 'Technical Approach Created',
      message: `${approachName} has been added to your library`,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="space-y-6 p-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Create Technical Approach
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Category Selection */}
          {!selectedCategory ? (
            <div className="space-y-4">
              <p className="text-gray-600">
                Select a category for your technical approach. This helps organize and classify your methodologies.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {APPROACH_CATEGORIES.map((category) => (
                  <Card 
                    key={category.id}
                    className="p-5 cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-yellow-500"
                    onClick={() => handleSelectCategory(category)}
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${category.color} flex items-center justify-center text-2xl`}>
                          {category.icon}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{category.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                      </div>
                      {category.examples.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {category.examples.map((example, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {example}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            /* Details Form */
            <div className="space-y-6">
              <div className={`p-6 rounded-xl bg-gradient-to-r ${selectedCategory.color} text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{selectedCategory.icon}</span>
                    <div>
                      <h3 className="text-xl font-bold">{selectedCategory.name}</h3>
                      <p className="opacity-90 text-sm">{selectedCategory.description}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedCategory(null)}
                    className="text-white hover:bg-white/20"
                  >
                    Change
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Approach Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={approachName}
                    onChange={(e) => setApproachName(e.target.value)}
                    placeholder="e.g., Multi-phase Soil Vapor Extraction"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide a clear description of this technical approach..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Methodology (Optional)
                  </label>
                  <textarea
                    value={methodology}
                    onChange={(e) => setMethodology(e.target.value)}
                    placeholder="Detail the step-by-step methodology, equipment, procedures, etc..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[150px]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Include specific procedures, equipment, standards, or protocols that define this approach
                  </p>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm">
                    <strong>💡 Tip:</strong> Well-documented technical approaches can be reused across multiple projects and workplans, saving time and ensuring consistency.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedCategory(null)}
                  className="flex-1"
                >
                  Back to Categories
                </Button>
                <Button 
                  onClick={handleCreate}
                  disabled={!approachName.trim() || !description.trim()}
                  className="flex-1"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Create Approach
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

